// /visa/$nationality/$visatype — landing page per nationality × visa type
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getVisaNationality, getVisaType } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { ContactCTA } from "@/components/seo/Blocks";
import { Plane, ShieldCheck, BadgeCheck, FileCheck2, Clock, Phone, MessageCircle, Check, X } from "lucide-react";
import VisaTranslateDeepLinks from "@/components/VisaTranslateDeepLinks";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/visa/$nationality/$visatype")({
  loader: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    const v = getVisaType(params.visatype);
    if (!n || !v) throw notFound();
    if (!n.topVisaTypes.includes(v.id)) throw notFound();
    return { nat: params.nationality, vt: params.visatype };
  },
  head: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    const v = getVisaType(params.visatype);
    if (!n || !v) return { meta: [{ title: ctaTitle("ไม่พบหน้านี้", "/visa/$nationality/$visatype", "th")}] };
    const url = `${SITE}/visa/${n.slug}/${v.id}`;
    const title = `${v.nameTh} (${v.code}) สำหรับคน${n.nameTh} ${n.flag} · ${v.serviceFee} | NYC`;
    const desc = `บริการ${v.nameTh}สำหรับชาว${n.nameTh} — ${v.tagline} · เสร็จ ${v.processingTime} · ${v.durationInitial} — สอบถามฟรี ไม่มีข้อผูกมัด`;
    return {
      meta: [
        { title: ctaTitle(title, "/visa/$nationality/$visatype", "th") }, { name: "description", content: ctaDesc(desc, "/visa/$nationality/$visatype", "th")},
        { name: "keywords", content: [...n.keywords, v.nameTh, v.code, v.name].join(", ") },
        { property: "og:title", content: ctaTitle(title, "/visa/$nationality/$visatype", "th")}, { property: "og:description", content: ctaDesc(desc, "/visa/$nationality/$visatype", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/visa/${n.slug}/${v.id}`, `/en/visa/${n.slug}/${v.id}`)],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"
        ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${v.nameTh}สำหรับคน${n.nameTh}`,
              serviceType: `${v.name} for ${n.demonymEn} nationals`,
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", url: SITE, telephone: "+66832494999" },
              offers: { "@type": "Offer", price: v.serviceFee.replace(/[^\d]/g, "").slice(0, 6) || "25000", priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "HowTo", "@id": `${url}#howto`,
              name: `วิธีขอ ${v.nameTh} สำหรับคน${n.nameTh}`,
              step: [
                { "@type": "HowToStep", position: 1, name: "ปรึกษาเบื้องต้น", text: "ส่ง Passport + เอกสารผ่าน LINE @nycli — ประเมินคุณสมบัติฟรี" },
                { "@type": "HowToStep", position: 2, name: "เตรียมเอกสาร", text: `รวบรวม ${v.documents.slice(0, 3).join(", ")}` },
                { "@type": "HowToStep", position: 3, name: "ยื่นที่สถานทูต/BOI/ตม.", text: `ยื่นเอกสารและรอผล ${v.processingTime}` },
                { "@type": "HowToStep", position: 4, name: "รับวีซ่า", text: `ได้วีซ่า ${v.durationInitial}` },
              ],
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "วีซ่า", item: `${SITE}/visa` },
                { "@type": "ListItem", position: 3, name: `คน${n.nameTh}`, item: `${SITE}/visa/${n.slug}` },
                { "@type": "ListItem", position: 4, name: v.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) }))
        ]),
      ],
    };
  },
  component: VisaPairPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้านี้</h1>
      <Link to="/visa" className="mt-4 inline-block text-gold underline">กลับไปยังรายการทั้งหมด</Link>
    </main>
  ),
});

function VisaPairPage() {
  const { nat, vt } = Route.useLoaderData() as { nat: string; vt: string };
  const n = getVisaNationality(nat)!;
  const v = getVisaType(vt)!;
  const otherVisas = n.topVisaTypes.filter((id) => id !== vt).slice(0, 6).map((id) => getVisaType(id)!).filter(Boolean);

  // Map nationality → translation language slug + apostille country slug (cross-linking add-ons)
  const LANG_MAP: Record<string, string> = {
    american: "english", canadian: "english", british: "english", australian: "english", "new-zealander": "english", irish: "english", "south-african": "english",
    german: "german", austrian: "german", swiss: "german",
    french: "french", belgian: "french",
    spanish: "spanish", mexican: "spanish", argentinian: "spanish", chilean: "spanish", colombian: "spanish", peruvian: "spanish",
    brazilian: "portuguese", portuguese: "portuguese",
    italian: "italian", russian: "russian", dutch: "dutch", swedish: "swedish", norwegian: "norwegian",
    chinese: "chinese", taiwanese: "chinese", "hong-konger": "chinese", singaporean: "chinese",
    japanese: "japanese", korean: "korean",
    vietnamese: "vietnamese", burmese: "burmese", cambodian: "khmer", malaysian: "malay-indonesian", indonesian: "malay-indonesian",
    saudi: "arabic", emirati: "arabic", qatari: "arabic", kuwaiti: "arabic",
  };
  const COUNTRY_MAP: Record<string, string> = {
    american: "usa", canadian: "canada", british: "uk", australian: "australia", "new-zealander": "new-zealand", irish: "ireland", "south-african": "south-africa",
    german: "germany", austrian: "austria", swiss: "switzerland",
    french: "france", belgian: "belgium", dutch: "netherlands",
    spanish: "spain", italian: "italy", portuguese: "portugal",
    swedish: "sweden", norwegian: "norway", danish: "denmark", finnish: "finland",
    russian: "russia", polish: "poland", czech: "czech", hungarian: "hungary", greek: "greece",
    mexican: "mexico", argentinian: "argentina", brazilian: "brazil", chilean: "chile", colombian: "colombia", peruvian: "peru",
    chinese: "china", japanese: "japan", korean: "south-korea", indian: "india", filipino: "philippines", singaporean: "singapore", "hong-konger": "hong-kong", israeli: "israel", turkish: "turkey",
    vietnamese: "vietnam", malaysian: "malaysia", indonesian: "indonesia", cambodian: "cambodia", burmese: "myanmar", laotian: "laos",
    emirati: "uae", saudi: "saudi-arabia", qatari: "qatar", kuwaiti: "kuwait", egyptian: "egypt",
  };
  const addonLang: string | undefined = LANG_MAP[n.slug];
  const addonApostille: string | undefined = COUNTRY_MAP[n.slug];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/visa" className="hover:text-gold">วีซ่า</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/visa/$nationality" params={{ nationality: n.slug }} className="hover:text-gold">{n.nameTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{v.code}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> {v.code} · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            {v.nameTh} สำหรับคน{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{v.tagline}</p>

          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ระยะเวลา", v: v.durationInitial },
              { k: "ค่าบริการ", v: v.serviceFee },
              { k: "ดำเนินการ", v: v.processingTime },
              { k: "Work Permit", v: v.workPermit },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white">{s.v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service ให้บริการ {v.nameTh} ({v.code}) สำหรับชาว{n.nameTh} — {v.tagline} ค่าบริการ {v.serviceFee} เสร็จ {v.processingTime} · {n.visaExempt > 0 ? `(เข้าไทยได้ ${n.visaExempt} วันโดยไม่ต้องขอวีซ่าก่อน)` : "(ต้องยื่นวีซ่าล่วงหน้าที่สถานทูตไทย)"}
        </blockquote>

        <section className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6">
            <h2 className="font-display text-xl font-extrabold text-emerald-900">ข้อดี</h2>
            <ul className="mt-4 space-y-2">
              {v.pros.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-slate-800">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-6">
            <h2 className="font-display text-xl font-extrabold text-rose-900">ข้อจำกัด</h2>
            <ul className="mt-4 space-y-2">
              {v.cons.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-slate-800">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" /> {c}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">เอกสารที่ต้องใช้</h2>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {v.documents.map((d) => (
              <li key={d} className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 p-3.5 text-sm text-slate-800">
                <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {d}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">ราคาแพ็กเกจ</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
            {v.nameTh} · ค่าบริการ {v.serviceFee}
          </h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รวมเตรียมเอกสาร + ยื่นแทน + ติดตามผล</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> เสร็จภายใน {v.processingTime}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ค่าธรรมเนียมราชการ {v.govFee}</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> รับประกันยื่นใหม่ฟรีถ้าไม่ผ่านจากความผิดเรา</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        {otherVisas.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วีซ่าอื่นที่คน{n.nameTh}เลือกได้</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {otherVisas.map((o) => (
                <Link key={o.id} to="/visa/$nationality/$visatype" params={{ nationality: n.slug, visatype: o.id }}
                  className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{o.code}</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-1 text-[11px] text-slate-500">{o.tagline}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {(addonLang || addonApostille) && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
              บริการเสริมที่คน{n.nameTh}ใช้บ่อยคู่กับ{v.nameTh}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              เตรียมเอกสารวีซ่าให้ครบในที่เดียว — แปลรับรอง · รับรอง MFA · Apostille · Notary Public
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {addonLang && (
                <Link
                  to="/translate/$language"
                  params={{ language: addonLang }}
                  className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">แปลรับรอง</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      แปลเอกสารเป็นภาษา{n.nameTh}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">รับรองคำแปล + ตรา MFA</div>
                  </div>
                </Link>
              )}
              {addonApostille && (
                <Link
                  to="/apostille/$country"
                  params={{ country: addonApostille }}
                  className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
                >
                  <div className="flex-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Apostille / Legalization</div>
                    <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                      รับรองเอกสารใช้ที่{n.countryEn}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">MFA + สถานทูต / Hague</div>
                  </div>
                </Link>
              )}
              <Link
                to="/notary"
                className="group flex min-h-[44px] items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/60"
              >
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Notary Public</div>
                  <div className="mt-0.5 font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                    Notarial Services Attorney
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">รับรองลายมือชื่อ · สำเนาถูกต้อง</div>
                </div>
              </Link>
            </div>
          </section>
        )}

        <VisaTranslateDeepLinks
          mode="th"
          visaTypeId={v.id}
          visaCode={v.code}
          language={addonLang}
          languageNameTh={`ภาษา${addonLang === "english" ? "อังกฤษ" : addonLang === "chinese" ? "จีน" : addonLang === "japanese" ? "ญี่ปุ่น" : addonLang === "korean" ? "เกาหลี" : addonLang === "german" ? "เยอรมัน" : addonLang === "french" ? "ฝรั่งเศส" : addonLang === "spanish" ? "สเปน" : addonLang === "italian" ? "อิตาลี" : addonLang === "portuguese" ? "โปรตุเกส" : addonLang === "russian" ? "รัสเซีย" : addonLang === "dutch" ? "ดัตช์" : addonLang === "swedish" ? "สวีเดน" : addonLang === "norwegian" ? "นอร์เวย์" : addonLang === "arabic" ? "อาหรับ" : addonLang === "vietnamese" ? "เวียดนาม" : addonLang === "burmese" ? "พม่า" : addonLang === "khmer" ? "เขมร" : addonLang === "malay-indonesian" ? "มลายู/อินโดฯ" : "ปลายทาง"}`}
          languageNameEn={addonLang ? addonLang.charAt(0).toUpperCase() + addonLang.slice(1) : ""}
          nationalityNameTh={`ชาว${n.nameTh}`}
          nationalityDemonymEn={n.demonymEn}
        />
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
